import React from 'react';

const XPProgressBar = ({ currentXP, totalXP, level, showDetails = true, onSpendXP }) => {
  // Client-side replication of server BattleService AC-like curve
  const LEVEL_XP_ANCHORS = [
    [1,   1000],
    [10,  22000],
    [20,  165000],
    [30,  1200000],
    [50,  13000000],
    [80,  200000000],
    [100, 400000000],
  ];

  const findBracket = (lvl) => {
    if (lvl <= LEVEL_XP_ANCHORS[0][0]) return [0, 0];
    for (let i = 0; i < LEVEL_XP_ANCHORS.length - 1; i++) {
      const L1 = LEVEL_XP_ANCHORS[i][0];
      const L2 = LEVEL_XP_ANCHORS[i + 1][0];
      if (lvl >= L1 && lvl <= L2) return [i, i + 1];
    }
    const last = LEVEL_XP_ANCHORS.length - 1;
    return [last, last];
  };

  const xpToNextLevel = (lvl) => {
    lvl = Math.max(1, Math.floor(lvl));
    const [i1, i2] = findBracket(lvl);
    const [L1, Y1] = LEVEL_XP_ANCHORS[i1];
    const [L2, Y2] = LEVEL_XP_ANCHORS[i2];
    if (i1 === i2) return Y1;
    const B = Math.log(Y2 / Y1) / Math.log(L2 / L1);
    const A = Y1 / Math.pow(L1, B);
    return Math.round(A * Math.pow(lvl, B));
  };

  const totalXpForLevel = (targetLevel) => {
    targetLevel = Math.max(1, Math.floor(targetLevel));
    let xp = 0;
    for (let L = 1; L < targetLevel; L++) {
      xp += xpToNextLevel(L);
    }
    return xp;
  };

  const baseForLevel = totalXpForLevel(level);
  const needForNext = xpToNextLevel(level);
  const xpInCurrentLevel = Math.max(0, totalXP - baseForLevel);
  const xpRemaining = Math.max(0, needForNext - xpInCurrentLevel);
  const progressPercent = needForNext > 0 ? Math.min(100, (xpInCurrentLevel / needForNext) * 100) : 0;

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-bold text-white">Experience</h3>
        <span className="text-sm text-gray-400">Level {level}</span>
      </div>

      {/* XP Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm text-gray-300 mb-1">
          <span>{xpInCurrentLevel.toLocaleString()} XP this level</span>
          <span>{xpRemaining.toLocaleString()} XP to next level</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-yellow-500 to-orange-500 h-3 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        <div className="text-xs text-gray-400 mt-1">
          {progressPercent.toFixed(1)}% toward Level {level + 1}
        </div>
      </div>

      {showDetails && (
        <div className="text-sm">
          <div className="flex justify-between items-center mb-3">
            <div>
              <div className="text-gray-400">Unspent XP</div>
              <div className="text-xl font-bold text-yellow-400">{currentXP.toLocaleString()}</div>
            </div>
            {onSpendXP && currentXP > 0 && (
              <button
                onClick={onSpendXP}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
              >
                Spend XP
              </button>
            )}
          </div>
          <div>
            <div className="text-gray-400">Total XP</div>
            <div className="text-xl font-bold text-orange-400">{totalXP.toLocaleString()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XPProgressBar;
